"use client";

import { Typography } from "@/components/custom/typography";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { environments } from "@/lib/config";
import DoohCMSLoginDialog from "@/modules/account/media-company-cms/templates/dooh-cms/login-cms";
import RegistrationCancelDialog from "@/modules/account/user/components/pop-up/RegistrationCancelDialog";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

export interface SelectMemberTypeProps {
  label: {
    title: string;
    subtitle: string;
    advertiserCardTitle: string;
    advertiserCardRole: string;
    mediaCardTitle: string;
    mediaCardRole: string;
    freeNoticeStart: string;
    freeNoticeBold: string;
    freeNoticeEnd: string;
    cmsTitle: string;
    cmsSubtitle: string;
    cmsButton: string;
    cancelTitle: string;
    cancelAction: string;
    helpTitle: string;
    helpAction: string;
  };
}

export default function SelectMemberType({ label }: SelectMemberTypeProps) {
  const [openCMSLogin, setOpenCMSLogin] = useState(false);
  const [openCancelSignUp, setOpenCancelSignUp] = useState(false);
  const [selectedType, setSelectedType] = useState<
    "advertising" | "media" | null
  >(null);

  return (
    <div className="min-w-3xl mx-auto ">
      <Typography variant="h2" className="mb-3 font-bold" tone={"medium"}>
        {label.title}
      </Typography>
      <Typography
        variant="body2"
        tone={"description"}
        className="whitespace-pre-line font-semibold"
      >
        {label.subtitle}
      </Typography>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 mt-8 ">
        <Card
          className={`shadow-2xl border-[0.5] rounded-xl shadow-custom border-gray-100 cursor-pointer transition-colors p-0 hover:border-primary-400 hover:border-1 whitespace-pre-line ${
            selectedType === "advertising"
              ? "bg-primary-400 border-primary-400 border-1"
              : ""
          }`}
          onMouseDown={() => setSelectedType("advertising")}
          onMouseUp={() => setSelectedType(null)}
          onMouseLeave={() => setSelectedType(null)}
        >
          <Link href="/advertising-agency/terms-agreement">
            <CardContent className="p-5 flex flex-col h-full justify-between">
              {}
              <div className="flex justify-between ">
                <div>
                  <Typography
                    variant="subheading2"
                    className={`mb-4 ${
                      selectedType === "advertising"
                        ? "text-white"
                        : "text-gray-800"
                    }`}
                  >
                    {label.advertiserCardTitle}
                  </Typography>
                  <Typography
                    variant={"body2"}
                    className={`mb-6 font-semibold ${
                      selectedType === "advertising"
                        ? "text-white"
                        : "text-primary-400"
                    }`}
                  >
                    {label.advertiserCardRole}
                  </Typography>
                </div>
                <div>
                  <Image
                    width={24}
                    height={24}
                    src={
                      selectedType === "advertising"
                        ? "/images/arrow-right-icon-active.svg"
                        : "/images/arrow-right-icon.svg"
                    }
                    alt="Arrow"
                    className="w-full h-auto mb-4 "
                  />
                </div>
              </div>

              <Image
                width={300}
                height={500}
                src={
                  selectedType === "advertising"
                    ? "/images/selectype-advertising-active.svg"
                    : "/images/selectype-advertising.svg"
                }
                alt="Advertising"
                className="w-full h-auto"
              />
            </CardContent>
          </Link>
        </Card>
        <Card
          className={`shadow-2xl border-[0.5] rounded-xl shadow-custom border-gray-100 cursor-pointer transition-colors p-0 hover:border-primary-400 hover:border-1 ${
            selectedType === "media"
              ? "bg-primary-400 border-primary-400 border-1"
              : ""
          }`}
          onMouseDown={() => setSelectedType("media")}
          onMouseUp={() => setSelectedType(null)}
          onMouseLeave={() => setSelectedType(null)}
        >
          <Link href="/cms-media/terms">
            <CardContent className="p-5 flex flex-col h-full justify-between">
              {}

              <div className="flex justify-between">
                <div>
                  <Typography
                    variant="subheading2"
                    className={`mb-4 whitespace-pre-line ${
                      selectedType === "media" ? "text-white" : "text-gray-800"
                    }`}
                  >
                    {label.mediaCardTitle}
                  </Typography>
                  <Typography
                    variant={"body2"}
                    className={`mb-6 font-semibold whitespace-pre-line ${
                      selectedType === "media"
                        ? "text-white"
                        : "text-primary-400"
                    }`}
                  >
                    {label.mediaCardRole}
                    <br />
                    <br />
                  </Typography>
                </div>

                <div>
                  <Image
                    width={24}
                    height={24}
                    src={
                      selectedType === "media"
                        ? "/images/arrow-right-icon-active.svg"
                        : "/images/arrow-right-icon.svg"
                    }
                    alt="Arrow"
                    className="w-full h-auto mb-4"
                  />
                </div>
              </div>
              <Image
                width={300}
                height={500}
                src={
                  selectedType === "media"
                    ? "/images/selectype-media-active.svg"
                    : "/images/selectype-media.svg"
                }
                alt="Media"
                className="w-full h-auto"
              />
            </CardContent>
          </Link>
        </Card>
      </div>

      <Button
        variant="secondary"
        className="text-xs font-medium text-gray-500 text-center bg-muted w-full pointer-events-none hover:bg-muted hover:border-gray-300"
      >
        <Typography variant={"body3"} tone={"description"}>
          {label.freeNoticeStart}{" "}
        </Typography>{" "}
        <Typography variant={"body3"} tone={"normal"}>
          {label.freeNoticeBold}
        </Typography>{" "}
        <Typography variant={"body3"} tone={"description"}>
          {label.freeNoticeEnd}
        </Typography>
      </Button>

      <div className="text-center my-12 flex justify-between items-center">
        <Typography variant={"body3"} tone={"subtle"}>
          {label.cmsTitle} {label.cmsSubtitle}
        </Typography>
        <Link
          href={`${environments.authServerUrl}?idp_hint=cms`}
          className="border border-gray-300 rounded-md py-4 px-5 flex"
        >
          <Image
            width={300}
            height={300}
            src="/images/cms.svg"
            alt="CMS"
            className="h-5 w-auto mr-2 inline"
          />
          <Typography variant={"body2"} className="font-semibold">
            {label.cmsButton}
          </Typography>
        </Link>
        <DoohCMSLoginDialog
          open={openCMSLogin}
          onClose={() => setOpenCMSLogin(false)}
        />
      </div>
      <div className="flex justify-between border-t-2 pt-6 border-gray-300">
        <Typography variant="body3" tone="subtle">
          {label.cancelTitle}{" "}
        </Typography>

        <button
          type="button"
          onClick={() => setOpenCancelSignUp(true)}
          className="text-gray-400 underline underline-offset-2 text-sm font-medium cursor-pointer"
        >
          {label.cancelAction}{" "}
        </button>
      </div>

      <RegistrationCancelDialog
        open={openCancelSignUp}
        setOpen={setOpenCancelSignUp}
      />
    </div>
  );
}
